import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { api, type BillingInvoice, type BillingOutletView, type BillingPricing } from '../lib/api.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

/**
 * Subscription & billing page (staff PWA only).
 *
 * Lists every outlet the signed-in user can access — including outlets whose
 * gateway or database hosting has already expired — and lets any PWA user of
 * the outlet buy a monthly/yearly renewal through the gateway owner's
 * ToyyibPay account.
 */
@customElement('billing-page')
export class BillingPage extends LitElement {
  @property() email = '';
  /** Outlet code to highlight/open from a push notification or deep link. */
  @property() focusOutlet = '';
  /** Render a single outlet without the global page header (outlet modal). */
  @property({ type: Boolean, reflect: true }) compact = false;
  @property() storeCode = '';

  @state() private _loading = true;
  @state() private _error = '';
  @state() private _pricing: BillingPricing | null = null;
  @state() private _outlets: BillingOutletView[] = [];
  /** False when the gateway has no checkout configured; expiry is still shown. */
  @state() private _billingEnabled = true;
  @state() private _toast = '';
  @state() private _toastTimer?: number;

  @state() private _checkingOut = '';
  @state() private _payingInvoice: BillingInvoice | null = null;
  @state() private _payError = '';
  @state() private _pollToken = 0;

  @state() private _historyOpen = false;
  @state() private _historyOutlet: BillingOutletView | null = null;
  @state() private _history: BillingInvoice[] = [];
  @state() private _historyLoading = false;
  @state() private _historyError = '';

  private _loadedFor = '';

  static styles = [TAB_UI_STYLES, css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
      padding: 1rem;
      box-sizing: border-box;
    }
    :host([compact]) {
      height: auto;
      min-height: 0;
      overflow: visible;
      padding: 0;
    }
    .page-scroll { flex: 1; min-height: 0; overflow-y: auto; }
    .top {
      display: flex;
      flex-wrap: wrap;
      gap: 0.6rem;
      align-items: center;
      margin-bottom: 0.75rem;
      flex-shrink: 0;
    }
    .top h2 { margin: 0; font-size: 1.05rem; }
    .top .hint { color: var(--color-text-secondary); font-size: 0.8rem; }
    .spacer { flex: 1; }
    .cards { display: grid; gap: 0.75rem; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); }
    .card {
      border: 1px solid var(--color-border);
      border-radius: var(--radius);
      background: var(--color-surface);
      padding: 0.85rem;
    }
    .card.focused { border-color: var(--color-primary); box-shadow: 0 0 0 2px rgba(37,99,235,0.15); }
    .card-head { display: flex; gap: 0.6rem; align-items: center; margin-bottom: 0.6rem; }
    .card-head .name { font-weight: 700; }
    .card-head .code { color: var(--color-text-secondary); font-size: 0.8rem; }
    .card-head .server { color: var(--color-text-secondary); font-size: 0.75rem; margin-left: auto; text-align: right; }
    .pill { display: inline-flex; align-items: center; gap: 0.25rem; border-radius: 999px; padding: 0.12rem 0.55rem; font-size: 0.72rem; font-weight: 600; background: #eef; }
    .pill.online { background: #e8f5e9; color: #1b5e20; }
    .pill.offline { background: #eceff1; color: #607d8b; }
    .pill.active { background: #e8f5e9; color: #1b5e20; }
    .pill.expiring { background: #fff8e1; color: #8a6d00; }
    .pill.expired { background: #ffebee; color: #c62828; }
    .pill.muted { background: #eceff1; color: #607d8b; }
    .sub {
      border-top: 1px solid var(--color-border);
      padding-top: 0.6rem;
      margin-top: 0.6rem;
    }
    .sub-title { display: flex; align-items: center; gap: 0.5rem; font-weight: 600; font-size: 0.9rem; }
    .sub-title .days { color: var(--color-text-secondary); font-weight: 400; font-size: 0.78rem; }
    .sub-meta { color: var(--color-text-secondary); font-size: 0.78rem; margin: 0.25rem 0 0.55rem; }
    .prices { display: flex; gap: 0.45rem; flex-wrap: wrap; }
    .price-btn {
      border: 1px solid var(--color-primary);
      color: var(--color-primary);
      background: transparent;
      border-radius: var(--radius);
      padding: 0.42rem 0.7rem;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
    }
    .price-btn:hover:not(:disabled) { background: rgba(37,99,235,0.08); }
    .price-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .pending {
      margin-top: 0.55rem;
      padding: 0.5rem 0.65rem;
      border-radius: var(--radius);
      background: #fff8e1;
      color: #8a6d00;
      font-size: 0.8rem;
      display: flex;
      gap: 0.5rem;
      align-items: center;
      flex-wrap: wrap;
    }
    .link-btn { border: 0; background: none; color: var(--color-primary); cursor: pointer; font-size: 0.8rem; padding: 0; text-decoration: underline; }
    .error { background: #fdecea; color: var(--color-error, #c0392b); padding: 0.6rem 0.85rem; border-radius: var(--radius); font-size: 0.85rem; margin-bottom: 0.75rem; }
    .notice {
      background: #fff8e1; border: 1px solid #ffe082; color: #6b4f00;
      padding: 0.55rem 0.85rem; border-radius: var(--radius); font-size: 0.82rem;
      margin-bottom: 0.75rem;
    }
    .state-message { text-align: center; padding: 2.5rem 1rem; color: var(--color-text-secondary); }
    .toast {
      position: fixed; left: 50%; bottom: 1.25rem; transform: translateX(-50%);
      background: #1f2937; color: #fff; padding: 0.6rem 1rem; border-radius: 999px;
      font-size: 0.82rem; z-index: 1000; max-width: 90vw;
    }
    .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: none; align-items: center; justify-content: center; z-index: 900; padding: 1rem; }
    .modal-overlay.open { display: flex; }
    .modal { background: var(--color-surface); border-radius: var(--radius); width: min(720px, 100%); max-height: 85vh; display: flex; flex-direction: column; overflow: hidden; }
    .modal-header { display: flex; align-items: center; gap: 0.5rem; padding: 0.8rem 1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; flex: 1; }
    .modal-close { border: 0; background: none; font-size: 1.3rem; cursor: pointer; }
    .modal-body { padding: 1rem; overflow: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
    th, td { text-align: left; padding: 0.35rem 0.4rem; border-bottom: 1px solid var(--color-border); }
    th.num, td.num { text-align: right; }
    .waiting { text-align: center; padding: 1rem; }
    .spinner { width: 28px; height: 28px; border: 3px solid #e5e7eb; border-top-color: var(--color-primary); border-radius: 50%; margin: 0 auto 0.75rem; animation: spin 0.8s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `];

  protected firstUpdated() {
    this._consumePaymentReturn();
    void this._load();
  }

  protected updated(changed: PropertyValues) {
    if (changed.has('email') || changed.has('focusOutlet') || changed.has('storeCode')) {
      const key = `${this.email}|${this.storeCode || this.focusOutlet}`;
      if (key !== this._loadedFor) void this._load();
    }
  }

  private async _load() {
    if (!this.email) return;
    this._loadedFor = `${this.email}|${this.storeCode || this.focusOutlet}`;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.billingOutlets();
      this._pricing = res.data.pricing;
      this._outlets = res.data.outlets ?? [];
      this._billingEnabled = res.data.billingEnabled !== false;
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Could not load subscriptions.';
    } finally {
      this._loading = false;
    }
  }

  private _consumePaymentReturn() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!(params.get('payment') || '').startsWith('return') && !params.get('order_id')) return;
      // ToyyibPay appends order_id/billcode/status_id to the return URL.
      // order_id is our invoice id (billExternalReferenceNo).
      const invoiceId = params.get('invoice') || params.get('order_id') || '';
      const url = new URL(window.location.href);
      for (const key of ['payment', 'invoice', 'order_id', 'billcode', 'status_id']) {
        url.searchParams.delete(key);
      }
      window.history.replaceState({}, '', url.toString());
      if (invoiceId) void this._pollInvoice(invoiceId, '');
    } catch {
      /* ignore malformed URL */
    }
  }

  private _money(cents: number): string {
    const currency = this._pricing?.currency || 'MYR';
    return `${currency === 'MYR' ? 'RM' : currency} ${(cents / 100).toFixed(2)}`;
  }

  private _date(epoch: number): string {
    if (!epoch) return '—';
    return new Date(epoch * 1000).toLocaleDateString('en-MY', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  }

  private _statusLabel(status: string): string {
    switch (status) {
      case 'active': return 'Active';
      case 'expiring': return 'Expiring soon';
      case 'expired': return 'Expired';
      case 'not_subscribed': return 'Not subscribed';
      case 'no_expiry': return 'No expiry';
      case 'not_applicable': return 'Not applicable';
      default: return status || 'Unknown';
    }
  }

  private _daysText(days: number, status: string): string {
    if (status === 'expired') return `${Math.abs(days)} day(s) ago`;
    if (status === 'expiring' || status === 'active') return `${days} day(s) left`;
    return '';
  }

  private async _renew(outlet: BillingOutletView, kind: 'gateway' | 'db', cycle: 'monthly' | 'yearly') {
    if (!outlet.canRenew || this._checkingOut) return;
    const key = `${outlet.uuid}:${kind}:${cycle}`;
    this._checkingOut = key;
    this._error = '';
    this._payError = '';
    // Open the tab synchronously so the browser treats it as part of the
    // click gesture (an await before window.open is blocked as a popup).
    const payWindow = window.open('', '_blank');
    if (payWindow) payWindow.opener = null;
    try {
      const res = await api.billingCheckout(outlet.uuid, kind, cycle);
      const invoice: BillingInvoice = {
        id: res.data.invoiceId,
        outletUuid: outlet.uuid,
        storeCode: outlet.code,
        kind,
        cycle,
        months: cycle === 'yearly' ? 12 : 1,
        amountCents: res.data.amountCents,
        currency: res.data.currency,
        status: 'pending',
        createdAt: Math.floor(Date.now() / 1000),
        paidAt: 0,
        appliedAt: 0,
        periodStart: 0,
        periodEnd: 0,
        paymentUrl: res.data.paymentUrl,
      };
      this._payingInvoice = invoice;
      if (payWindow) {
        payWindow.location.href = res.data.paymentUrl;
      } else {
        window.location.href = res.data.paymentUrl;
      }
      void this._pollInvoice(invoice.id, outlet.code);
    } catch (err) {
      payWindow?.close();
      this._error = err instanceof Error ? err.message : 'Could not create the payment.';
    } finally {
      this._checkingOut = '';
    }
  }

  private async _pollInvoice(invoiceId: string, outletCode: string) {
    const token = ++this._pollToken;
    this._payError = '';
    for (let i = 0; i < 40; i++) {
      if (token !== this._pollToken) return;
      try {
        const res = await api.billingRequery(invoiceId);
        if (token !== this._pollToken) return;
        const invoice = res.data;
        this._payingInvoice = invoice;
        if (invoice.status === 'applied') {
          this._showToast(`${outletCode || invoice.storeCode || 'Outlet'} renewed until ${this._date(invoice.periodEnd)}.`);
          this._payingInvoice = null;
          void this._load();
          this.dispatchEvent(new CustomEvent('subscription-renewed', {
            bubbles: true, composed: true,
            detail: { outletCode: invoice.storeCode, kind: invoice.kind },
          }));
          return;
        }
        if (invoice.status === 'apply_pending') {
          this._showToast('Payment received — waiting for the outlet server to come online.');
          this._payingInvoice = null;
          void this._load();
          return;
        }
        if (invoice.status === 'failed' || invoice.status === 'expired') {
          this._payError = invoice.lastError || 'The payment could not be completed.';
          return;
        }
      } catch (err) {
        if (token !== this._pollToken) return;
        this._payError = err instanceof Error ? err.message : 'Could not verify the payment yet.';
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    this._payError = 'Still waiting for ToyyibPay. You can reopen the payment page or check again later.';
  }

  private _showToast(message: string) {
    this._toast = message;
    if (this._toastTimer) window.clearTimeout(this._toastTimer);
    this._toastTimer = window.setTimeout(() => { this._toast = ''; }, 6000);
  }

  private _closePayment() {
    this._pollToken++;
    this._payingInvoice = null;
    this._payError = '';
  }

  private async _openHistory(outlet: BillingOutletView) {
    this._historyOpen = true;
    this._historyOutlet = outlet;
    this._history = [];
    this._historyError = '';
    this._historyLoading = true;
    try {
      const res = await api.billingInvoices(outlet.uuid);
      this._history = res.data ?? [];
    } catch (err) {
      this._historyError = err instanceof Error ? err.message : 'Could not load history.';
    } finally {
      this._historyLoading = false;
    }
  }

  private _renderSubscription(
    outlet: BillingOutletView,
    kind: 'gateway' | 'db',
    title: string,
    state: BillingOutletView['gateway'] | BillingOutletView['db'],
    prices: { monthlyCents: number; yearlyCents: number },
  ) {
    const enforced = kind === 'gateway' ? true : (state as BillingOutletView['db']).enforced;
    const forever = kind === 'db' && (state as BillingOutletView['db']).forever;
    // An outlet explicitly removed from the gateway is not sold a gateway
    // subscription again from here; it must be re-enabled in the POS first.
    const declared = kind === 'db' ? true : outlet.declared;
    const canBuy = this._billingEnabled && outlet.canRenew && declared && enforced && !forever;
    const disabled = !canBuy || !!this._checkingOut;

    if (kind === 'gateway' && !outlet.declared) {
      return html`
        <div class="sub">
          <div class="sub-title">${title} <span class="pill muted">Not activated</span></div>
          <div class="sub-meta">Enable this outlet from the POS Gateway Subscription dialog to start billing.</div>
        </div>`;
    }

    if (kind === 'db' && !enforced) {
      return html`
        <div class="sub">
          <div class="sub-title">${title} <span class="pill muted">Not applicable</span></div>
          <div class="sub-meta">This outlet's ppServer runs locally and never expires.</div>
        </div>`;
    }
    if (kind === 'db' && forever) {
      return html`
        <div class="sub">
          <div class="sub-title">${title} <span class="pill muted">No expiry</span></div>
          <div class="sub-meta">Database hosting for this outlet does not expire.</div>
        </div>`;
    }

    return html`
      <div class="sub">
        <div class="sub-title">
          ${title}
          <span class="pill ${state.status}">${this._statusLabel(state.status)}</span>
          <span class="days">${this._daysText(state.daysLeft, state.status)}</span>
        </div>
        <div class="sub-meta">
          ${state.subscribed || state.status === 'expired'
            ? html`Expires ${this._date(state.expiresAt)}`
            : html`Not subscribed`}
        </div>
        <div class="prices">
          <button class="price-btn" ?disabled=${disabled}
            @click=${() => this._renew(outlet, kind, 'monthly')}>
            ${this._checkingOut === `${outlet.uuid}:${kind}:monthly` ? 'Starting…' : `${this._money(prices.monthlyCents)} / month`}
          </button>
          <button class="price-btn" ?disabled=${disabled}
            @click=${() => this._renew(outlet, kind, 'yearly')}>
            ${this._checkingOut === `${outlet.uuid}:${kind}:yearly` ? 'Starting…' : `${this._money(prices.yearlyCents)} / year`}
          </button>
        </div>
        ${outlet.pendingInvoice && outlet.pendingInvoice.kind === kind
          ? html`
              <div class="pending">
                ${outlet.pendingInvoice.status === 'apply_pending'
                  ? html`<span>Payment received — applying to the outlet server…</span>`
                  : html`<span>Payment pending — ${this._money(outlet.pendingInvoice.amountCents)}</span>`}
                ${outlet.pendingInvoice.status !== 'apply_pending' && outlet.pendingInvoice.paymentUrl
                  ? html`<a class="link-btn" href=${outlet.pendingInvoice.paymentUrl} target="_blank" rel="noopener">Pay now</a>`
                  : nothing}
                <button class="link-btn" @click=${() => this._pollInvoice(outlet.pendingInvoice!.id, outlet.code)}>Check status</button>
              </div>` : nothing}
      </div>`;
  }

  protected render() {
    if (this._loading) {
      return html`<div class="state-message">${this.compact ? 'Loading subscription…' : 'Loading subscriptions…'}</div>`;
    }

    const outlets = this.storeCode
      ? this._outlets.filter((o) => o.code === this.storeCode)
      : this._outlets;

    return html`
      ${this.compact ? nothing : html`
        <div class="top">
          <h2>Subscriptions & Billing</h2>
          <span class="hint">Payments go to the PharmaPOS gateway owner's ToyyibPay account.</span>
          <span class="spacer"></span>
          <button class="btn" @click=${() => this._load()}>Refresh</button>
        </div>`}
      ${this._error ? html`<div class="error">${this._error}</div>` : nothing}
      ${!this._billingEnabled && !this._loading
        ? html`<div class="notice">
            Renewals are not configured on this gateway. The expiry dates below are read live
            from each outlet server, so they are still accurate.
          </div>`
        : nothing}
      <div class=${this.compact ? '' : 'page-scroll'}>
        ${outlets.length === 0
          ? html`<div class="state-message">${this.compact
              ? 'This outlet is not available for billing on this gateway.'
              : html`No outlets are available for this account.<br>
                  Ask an outlet admin to grant PWA access, or register a store first.`}</div>`
          : html`<div class="cards">
              ${outlets.map((outlet) => html`
                <div class="card ${(this.storeCode || this.focusOutlet) === outlet.code ? 'focused' : ''}">
                  <div class="card-head">
                    <div>
                      <div class="name">${outlet.name || outlet.code}</div>
                      <div class="code">${outlet.code}</div>
                    </div>
                    <span class="pill ${outlet.serverOnline ? 'online' : 'offline'}">
                      ${outlet.serverOnline ? 'Online' : 'Offline'}
                    </span>
                    <div class="server">${outlet.serverName || outlet.serverId}</div>
                  </div>
                  ${this._renderSubscription(outlet, 'gateway', 'Gateway subscription', outlet.gateway, {
                    monthlyCents: this._pricing?.gateway.monthlyCents ?? 8000,
                    yearlyCents: this._pricing?.gateway.yearlyCents ?? 80000,
                  })}
                  ${this._renderSubscription(outlet, 'db', 'Database hosting', outlet.db, {
                    monthlyCents: this._pricing?.db.monthlyCents ?? 6000,
                    yearlyCents: this._pricing?.db.yearlyCents ?? 60000,
                  })}
                  <div class="sub" style="text-align:right">
                    <button class="link-btn" @click=${() => this._openHistory(outlet)}>Payment history</button>
                  </div>
                </div>`)}
            </div>`}
      </div>

      <!-- Payment waiting -->
      <div class="modal-overlay ${this._payingInvoice ? 'open' : ''}" @click=${this._closePayment}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>Waiting for payment</h3>
            <button class="modal-close" @click=${this._closePayment}>&times;</button>
          </div>
          <div class="modal-body waiting">
            ${this._payError ? html`<div class="error">${this._payError}</div>` : html`<div class="spinner"></div>`}
            <p>${this._payingInvoice
              ? html`Invoice <strong>${this._payingInvoice.id}</strong> — ${this._money(this._payingInvoice.amountCents)}`
              : nothing}</p>
            <p class="hint">Complete the payment in the ToyyibPay tab. This page updates automatically.</p>
            ${this._payingInvoice?.paymentUrl
              ? html`<p><a class="link-btn" href=${this._payingInvoice.paymentUrl} target="_blank" rel="noopener">Open the payment page again</a></p>`
              : nothing}
          </div>
        </div>
      </div>

      <!-- History -->
      <div class="modal-overlay ${this._historyOpen ? 'open' : ''}" @click=${() => (this._historyOpen = false)}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>Payment history — ${this._historyOutlet?.name || this._historyOutlet?.code || ''}</h3>
            <button class="modal-close" @click=${() => (this._historyOpen = false)}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._historyLoading ? html`<div class="state-message">Loading…</div>` : nothing}
            ${this._historyError ? html`<div class="error">${this._historyError}</div>` : nothing}
            ${!this._historyLoading && !this._historyError && this._history.length === 0
              ? html`<div class="state-message">No payments yet.</div>` : nothing}
            ${this._history.length > 0 ? html`
              <table>
                <thead><tr><th>Invoice</th><th>Kind</th><th>Cycle</th><th class="num">Amount</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  ${this._history.map((inv) => html`
                    <tr>
                      <td>${inv.id}</td>
                      <td>${inv.kind === 'db' ? 'Database hosting' : 'Gateway'}</td>
                      <td>${inv.cycle}</td>
                      <td class="num">${this._money(inv.amountCents)}</td>
                      <td>${inv.status}</td>
                      <td>${this._date(inv.createdAt)}</td>
                    </tr>`)}
                </tbody>
              </table>` : nothing}
          </div>
        </div>
      </div>

      ${this._toast ? html`<div class="toast">${this._toast}</div>` : nothing}
    `;
  }
}
